import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Key, CheckCircle, XCircle, FloppyDisk, Trash, Plus,
  ArrowSquareOut, ArrowsClockwise, Eye, EyeSlash,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApiManager() {
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [drafts, setDrafts] = useState({});
  const [reveal, setReveal] = useState({});
  const [customOpen, setCustomOpen] = useState(false);

  const fetchApis = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/system-apis`, { withCredentials: true });
      setApis(r.data?.apis || []);
    } catch (e) {
      setMessage(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApis(); }, [fetchApis]);

  const setDraft = (id, value) => setDrafts((d) => ({ ...d, [id]: value }));

  const save = async (id, body) => {
    setMessage("");
    try {
      await axios.put(`${API}/admin/system-apis/${id}`, body, { withCredentials: true });
      setMessage(`Saved ${id} key`);
      setDrafts((d) => { const c = { ...d }; delete c[id]; return c; });
      fetchApis();
    } catch (e) {
      setMessage(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Remove the ${id} key? Backend will revert to the fallback (DEMO_KEY for NASA, empty otherwise).`)) return;
    try {
      await axios.delete(`${API}/admin/system-apis/${id}`, { withCredentials: true });
      setMessage(`Removed ${id} key`);
      fetchApis();
    } catch (e) {
      setMessage(e.response?.data?.detail || "Delete failed");
    }
  };

  const test = async (id) => {
    setMessage(`Testing ${id}...`);
    try {
      const r = await axios.post(`${API}/admin/system-apis/${id}/test`, { value: drafts[id] || "" }, { withCredentials: true });
      setMessage(`${id} test: ${r.data.ok ? "PASS" : "FAIL"} — ${r.data.status_text}`);
      fetchApis();
    } catch (e) {
      setMessage(`Test failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-4" data-testid="api-manager">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-semibold mb-1">API Manager</h2>
          <p className="text-xs text-stone-500 font-body max-w-2xl">
            Store API keys for every external service Conexus Foundation uses. Keys take effect within 60 seconds without restart.
            Test buttons probe the real provider to confirm the key works.
          </p>
        </div>
        <button onClick={fetchApis} className="text-xs text-stone-400 hover:text-white flex items-center gap-1" data-testid="api-refresh-btn">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {message && (
        <div className="bg-emerald-600/10 border border-emerald-600/30 text-emerald-300 text-xs px-3 py-2 rounded-lg" data-testid="api-message">
          {message}
        </div>
      )}

      {loading ? (
        <div className="text-stone-500 text-sm py-10 text-center">Loading...</div>
      ) : (
        <div className="space-y-3">
          {apis.map((api) => (
            <ApiRow
              key={api.id}
              api={api}
              draft={drafts[api.id]}
              setDraft={(v) => setDraft(api.id, v)}
              reveal={reveal[api.id]}
              toggleReveal={() => setReveal((r) => ({ ...r, [api.id]: !r[api.id] }))}
              onSave={(body) => save(api.id, body)}
              onTest={() => test(api.id)}
              onDelete={() => remove(api.id)}
            />
          ))}

          {/* Add custom API */}
          <button
            onClick={() => setCustomOpen(!customOpen)}
            className="w-full text-xs text-emerald-400 hover:text-emerald-300 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/10 border border-emerald-600/30 border-dashed"
            data-testid="api-add-custom-btn"
          >
            <Plus size={12} weight="bold" /> {customOpen ? "Cancel" : "Add custom API"}
          </button>
          {customOpen && <CustomApiForm onSave={(body) => save(body.id, body).then(() => setCustomOpen(false))} />}
        </div>
      )}
    </div>
  );
}

function ApiRow({ api, draft, setDraft, reveal, toggleReveal, onSave, onTest, onDelete }) {
  const value = draft !== undefined ? draft : "";
  const dirty = draft !== undefined && draft !== "";

  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 p-4" data-testid={`api-row-${api.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${api.is_set ? "bg-emerald-600/20" : "bg-stone-800"}`}>
          <Key size={16} weight="duotone" className={api.is_set ? "text-emerald-400" : "text-stone-500"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-heading font-medium text-white">{api.label}</h3>
            {api.is_set ? (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400">Set</span>
            ) : (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-700 text-stone-400">Not set</span>
            )}
            {api.test_status === "ok" && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white flex items-center gap-1">
                <CheckCircle size={9} /> Verified
              </span>
            )}
            {api.test_status === "fail" && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600/20 text-red-400 flex items-center gap-1">
                <XCircle size={9} /> {api.test_status_text}
              </span>
            )}
          </div>
          <p className="text-xs text-stone-500 leading-relaxed mb-1">{api.description}</p>
          {api.signup_url && (
            <a href={api.signup_url} target="_blank" rel="noopener noreferrer"
              className="text-[11px] text-sky-400 hover:text-sky-300 flex items-center gap-1 w-fit"
              data-testid={`api-signup-${api.id}`}>
              Get a key <ArrowSquareOut size={10} />
            </a>
          )}
          {api.is_set && (
            <p className="text-[11px] text-stone-600 mt-1 font-mono">Current: {api.masked}</p>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex-1 min-w-[200px] relative">
          <input
            type={reveal ? "text" : "password"}
            value={value}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={api.is_set ? "Enter new key to replace" : "Paste API key here"}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg pl-3 pr-9 py-2 text-sm text-white font-mono focus:outline-none focus:border-emerald-500"
            data-testid={`api-input-${api.id}`}
          />
          <button
            type="button"
            onClick={toggleReveal}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
            data-testid={`api-reveal-${api.id}`}
          >
            {reveal ? <EyeSlash size={14} /> : <Eye size={14} />}
          </button>
        </div>
        <button
          onClick={() => onSave({ value })}
          disabled={!dirty}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-700 disabled:text-stone-500 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"
          data-testid={`api-save-${api.id}`}
        >
          <FloppyDisk size={12} weight="bold" /> Save
        </button>
        <button
          onClick={onTest}
          className="bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs px-3 py-2 rounded-lg"
          data-testid={`api-test-${api.id}`}
        >
          Test
        </button>
        {api.is_set && (
          <button
            onClick={onDelete}
            className="bg-stone-800 hover:bg-red-600/20 text-red-400 text-xs px-3 py-2 rounded-lg flex items-center gap-1"
            data-testid={`api-delete-${api.id}`}
          >
            <Trash size={12} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

function CustomApiForm({ onSave }) {
  const [id, setId] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState("");

  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 p-4 space-y-2" data-testid="api-custom-form">
      <p className="text-xs text-stone-400 font-medium">Add a Custom API</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input value={id} onChange={(e) => setId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
          placeholder="service id (e.g., stripe)"
          className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white font-mono"
          data-testid="api-custom-id" />
        <input value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Display label"
          className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="api-custom-label" />
      </div>
      <input value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="What is it used for?"
        className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white"
        data-testid="api-custom-desc" />
      <input value={value} onChange={(e) => setValue(e.target.value)}
        placeholder="API key value"
        className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white font-mono"
        data-testid="api-custom-value" />
      <button
        onClick={() => onSave({ id, label, description, value })}
        disabled={!id || !value}
        className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-700 text-white text-xs px-3 py-2 rounded-lg"
        data-testid="api-custom-save"
      >
        Add API
      </button>
    </div>
  );
}
